import { useCallback, useMemo, useState } from 'react';
import type { EmployeeType } from '../../services/api/models/EmployeeType';
import type { EmployeeTypeAssignmentItem } from '../../services/api';
import type { AgencyClinicResponse, OrgMemberResponse } from './types';

export type EmployeeTypeRow = {
  clinic_id: string;
  clinic_name: string;
  employee_type: EmployeeType | null;
  employee_type_other: string;
};

function buildSavedRows(member: OrgMemberResponse | null): EmployeeTypeRow[] {
  const assignments = member?.clinic_assignments ?? [];
  return assignments
    .map(a => ({
      clinic_id: a.clinic_id,
      clinic_name: a.clinic_name,
      employee_type: a.employee_type ?? null,
      employee_type_other: a.employee_type_other ?? '',
    }))
    .sort((a, b) => a.clinic_name.localeCompare(b.clinic_name));
}

function rowsEqual(a: EmployeeTypeRow[], b: EmployeeTypeRow[]): boolean {
  if (a.length !== b.length) return false;
  return a.every(
    (row, i) =>
      row.clinic_id === b[i].clinic_id &&
      row.employee_type === b[i].employee_type &&
      row.employee_type_other === b[i].employee_type_other
  );
}

type UseEmployeeTypesOptions = {
  /** Agency clinics, already carrying resolved display names. */
  clinics?: AgencyClinicResponse[];
  /**
   * Clinics the member will still have when this dialog saves. Rows for
   * anything else are dropped, so revoking a clinic stops offering an employee
   * type for it. Omit to show every saved row.
   */
  visibleClinicIds?: ReadonlySet<string>;
};

/** `member` must be referentially stable — ClinicianManager holds it in state. */
export function useEmployeeTypes(
  member: OrgMemberResponse | null,
  options?: UseEmployeeTypesOptions
) {
  const { clinics, visibleClinicIds } = options ?? {};
  const savedRows = useMemo(() => buildSavedRows(member), [member]);
  const [rows, setRows] = useState<EmployeeTypeRow[]>(savedRows);
  const [seededMember, setSeededMember] = useState(member);

  // Re-seed when a different member arrives. Both obvious alternatives are
  // wrong: `savedRows` identity is a useMemo result, which React may drop and
  // re-seed over edits in progress; comparing rows by value misses a member
  // switch when the two have matching saved rows, and the dialog never
  // unmounts (`open={!!member}`) so that state would carry across.
  if (seededMember !== member) {
    setSeededMember(member);
    setRows(savedRows);
  }

  const isDirty = useMemo(() => !rowsEqual(rows, savedRows), [rows, savedRows]);

  const setEmployeeType = useCallback(
    (clinicId: string, employeeType: EmployeeType | null) => {
      setRows(prev =>
        prev.map(row =>
          row.clinic_id === clinicId
            ? {
                ...row,
                employee_type: employeeType,
                employee_type_other:
                  employeeType === 'OTHER' ? row.employee_type_other : '',
              }
            : row
        )
      );
    },
    []
  );

  const setEmployeeTypeOther = useCallback((clinicId: string, text: string) => {
    setRows(prev =>
      prev.map(row =>
        row.clinic_id === clinicId ? { ...row, employee_type_other: text } : row
      )
    );
  }, []);

  // Filtered on the way out rather than in state, so unchecking a clinic and
  // checking it again brings the employee type back rather than losing it.
  const visibleRows = useMemo(
    () =>
      visibleClinicIds
        ? rows.filter(row => visibleClinicIds.has(row.clinic_id))
        : rows,
    [rows, visibleClinicIds]
  );

  /** Rows with OTHER selected but no free text — blocks saving. */
  const invalidClinicIds = useMemo(
    () =>
      new Set(
        visibleRows
          .filter(
            row =>
              row.employee_type === 'OTHER' && !row.employee_type_other.trim()
          )
          .map(row => row.clinic_id)
      ),
    [visibleRows]
  );

  const getAssignments = useCallback(
    (): EmployeeTypeAssignmentItem[] =>
      visibleRows.map(row => ({
        clinic_id: row.clinic_id,
        employee_type: row.employee_type,
        employee_type_other:
          row.employee_type === 'OTHER' ? row.employee_type_other.trim() : null,
      })),
    [visibleRows]
  );

  // Overlaid on the way out rather than seeded into state: the member payload
  // names each clinic itself and leaves that blank for an unnamed clinic, and
  // the clinic list can resolve after the rows are seeded. Nothing but the
  // label reads this field, so state stays untouched. Sorted again here
  // because the seeded order put the blank name first.
  const namedRows = useMemo(() => {
    if (!clinics?.length) return visibleRows;
    const clinicNameById = new Map(
      clinics.map(clinic => [clinic.id, clinic.name])
    );
    return visibleRows
      .map(row => {
        if (row.clinic_name?.trim()) return row;
        const resolved = clinicNameById.get(row.clinic_id);
        return resolved ? { ...row, clinic_name: resolved } : row;
      })
      .sort((a, b) => a.clinic_name.localeCompare(b.clinic_name));
  }, [visibleRows, clinics]);

  return {
    rows: namedRows,
    isDirty,
    invalidClinicIds,
    setEmployeeType,
    setEmployeeTypeOther,
    getAssignments,
  };
}
